import React, { useEffect, useState } from 'react';
import ConversationV2 from '../Conversations/ConversationV2';
import { useAppDispatch, useAppSelector } from "src/store";
import ConversationsList from './ConversationsList';
import Box from '@mui/system/Box';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import { Theme, SxProps, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toggleOpenConversation, fetchConversationWithMessages, fetchConversationsWithMessages } from 'src/features/account/conversations.slice';
import { fetchPriorityConversations } from 'src/features/account/filteredConversations.slice';
import ConversationTooltipManager from './ConversationTooltipManager';
import { useLocation } from 'react-router-dom';

interface StyleProps {
  box: SxProps<Theme>;
  dialog: SxProps<Theme>;
  wrapper: SxProps<Theme>;
  contentContainer: SxProps<Theme>;
}

const getStyles = (darkMode: boolean, theme: any, currentConversationId: string | null, isMobile: boolean, isTablet: boolean, drawerWidth: number): StyleProps => ({
  box: {
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    width: '100%',
    overflow: 'hidden',
    bgcolor: darkMode ? 'background.default' : 'background.paper',
    color: darkMode ? 'grey.300' : 'text.primary',
    transition: 'all 0.3s ease',
    position: 'relative',
  },
  dialog: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: '100vw', sm: '100vw', md: '750px', lg: '850px', xl: '950px' },
    '& .MuiDialog-paper': {
      width: { xs: '100vw', sm: '100vw', md: '750px', lg: '850px', xl: '950px' },
      color: darkMode ? 'grey.300' : 'text.primary',
      transition: 'all 0.3s ease',
      '& ::-webkit-scrollbar': {
        width: '8px',
        height: '8px'
      },
      '& ::-webkit-scrollbar-track': {
        bgcolor: darkMode ? 'grey.800' : 'grey.100'
      },
      '& ::-webkit-scrollbar-thumb': {
        bgcolor: darkMode ? 'grey.700' : 'grey.300',
        borderRadius: '4px',
        '&:hover': {
          bgcolor: darkMode ? 'grey.600' : 'grey.400'
        }
      }
    },
    '& .MuiBackdrop-root': {
      bgcolor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
    }
  },
  wrapper: {
    color: darkMode ? 'grey.100' : 'text.primary',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    height: '100%',
    width: '100%',
  },
});

const ConversationsWrapperComponent = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const openConversationId = useAppSelector(state => state.conversations.openConversationId);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { darkMode } = useAppSelector(state => state.theme);
  const theme = useTheme();
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false);
  const [hasLoadedFromUrl, setHasLoadedFromUrl] = useState(false);

  // Use useMemo to avoid re-parsing URL on every render
  const urlConversationId = React.useMemo(() => {
    return new URLSearchParams(location.search).get('conversation_id');
  }, [location.search]);
  
  // Prioritize Redux state over URL (URL only matters on initial load)
  const currentConversationId = openConversationId;

  // Load conversations when this page is first accessed
  useEffect(() => {
    // Fetch all active conversations on initial load
    dispatch(fetchConversationsWithMessages({ 
      limit: 25,  // Fetch a reasonable amount for initial load
      messagesLimit: 3,
      full_payload: false,  // Use minimal payload for initial load
      status: 'all_active'  // Fetch all active conversations (excludes archived/closed)
    }));
    
    // Also fetch priority conversations on initial load
    dispatch(fetchPriorityConversations());
  }, [dispatch]);

  // Only load from URL on initial mount
  useEffect(() => {
    if (!hasLoadedFromUrl && urlConversationId && urlConversationId !== 'null' && urlConversationId !== 'undefined') {
      setIsLoadingFromUrl(true);
      setHasLoadedFromUrl(true);

      // Open the conversation from URL
      dispatch(toggleOpenConversation(urlConversationId));

      dispatch(fetchConversationWithMessages({ conversationId: urlConversationId }))
        .then((result) => {
          console.log(`ConversationsWrapper: Successfully loaded conversation ${urlConversationId} from URL`);
        })
        .catch((error) => {
          console.error(`ConversationsWrapper: Failed to load conversation ${urlConversationId}:`, error);
        })
        .finally(() => {
          setIsLoadingFromUrl(false);
        });
    }
  }, [dispatch, hasLoadedFromUrl, urlConversationId]); // Dependencies for URL loading

  const handleCloseDialog = () => {
    dispatch(toggleOpenConversation(null));
    // Optionally clear URL params on close
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', window.location.pathname);
    }
    // On mobile, we need to ensure any touch events are properly reset
    if (isSmallScreen) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  /* --------------------- Responsive conversation viewer -------------------- */
  const ThreadDisplay = () => {
    // Guard against invalid ids
    if (!currentConversationId || currentConversationId === 'null' || currentConversationId === 'undefined') {
      return null;
    }

    const conversationNode = (
      <ConversationV2 conversationId={currentConversationId} onClose={handleCloseDialog} />
    );

    // Mobile: Full screen dialog
    if (isSmallScreen) {
      return (
        <Dialog
          open={true}
          onClose={handleCloseDialog}
          fullScreen
          closeAfterTransition
          sx={styles.dialog}
          disableEscapeKeyDown={false}
          disableScrollLock={true}
          PaperProps={{
            elevation: darkMode ? 0 : 24,
            sx: {
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }
          }}
          TransitionProps={{
            onExited: () => {
              window.history.pushState({}, '', window.location.pathname);
            }
          }}
          slotProps={{
            backdrop: {
              onClick: handleCloseDialog,
              sx: {
                '@media (max-width: 600px)': {
                  touchAction: 'none'
                }
              }
            }
          }}
        >
          {conversationNode}
        </Dialog>
      );
    }

    // Desktop: Right drawer (similar to ContactsFilterBar)
    if (isDesktop) {
      return (
        <Drawer
          anchor="right"
          open={true}
          onClose={handleCloseDialog}
          variant="persistent"
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: '100%', md: '600px', lg: '700px', xl: '800px' },
              maxWidth: '100vw',
              height: '100vh',
              bgcolor: darkMode ? 'background.paper' : 'background.default',
              color: darkMode ? 'grey.300' : 'text.primary',
              borderLeft: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
              transition: 'all 0.3s ease',
              boxShadow: darkMode ? '0 0 20px rgba(0, 0, 0, 0.5)' : '0 0 20px rgba(0, 0, 0, 0.1)',
            }
          }}
          ModalProps={{
            keepMounted: true,
            disableScrollLock: true,
          }}
        >
          {conversationNode}
        </Drawer>
      );
    }

    // Tablet: Modal-like drawer
    return (
      <Drawer
        anchor="right"
        open={true}
        onClose={handleCloseDialog}
        variant="temporary"
        sx={{
                      '& .MuiDrawer-paper': {
              width: '80%',
              maxWidth: '100vw',
              height: '100vh',
              bgcolor: darkMode ? 'background.paper' : 'background.default',
              color: darkMode ? 'grey.300' : 'text.primary',
              borderLeft: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
              transition: 'all 0.3s ease',
              boxShadow: darkMode ? '0 0 20px rgba(0, 0, 0, 0.5)' : '0 0 20px rgba(0, 0, 0, 0.1)',
            }
        }}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
        }}
      >
        {conversationNode}
      </Drawer>
    );
  };

  // Dynamic drawer width based on screen size
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const drawerWidth = isLargeScreen ? 520 : 420;

  const styles = getStyles(darkMode, theme, currentConversationId, isMobile, isTablet, drawerWidth);

  return (
    <ConversationTooltipManager>
      <Box sx={styles.wrapper}>
        {/* Main content area */}
        <Box sx={styles.contentContainer}>
          <ConversationsList conversationsListStyle="open" handleConversationsListToggleClick={() => { }} />
        </Box>
        
        {/* Desktop: Fixed right sidebar for conversation */}
        {isDesktop && currentConversationId && (
          <Box
            sx={{
              position: 'fixed',
              right: 0,
              bottom: 0,
 
              height: '100vh', // Continue here
              width: { md: '600px', lg: '700px', xl: '800px' },
              transition: 'transform 0.3s ease',
              transform: currentConversationId ? 'translateX(0)' : 'translateX(100%)',
              overflow: 'hidden',
              boxShadow: darkMode ? '0 0 20px rgba(0, 0, 0, 0.5)' : '0 0 20px rgba(0, 0, 0, 0.1)',
              zIndex: 1200,
              bgcolor: darkMode ? 'background.paper' : 'background.default',
              borderLeft: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <ConversationV2 conversationId={currentConversationId} onClose={handleCloseDialog} />
          </Box>
        )}
        
        {/* Mobile/Tablet: Dialog/Drawer */}
        {!isDesktop && currentConversationId && <ThreadDisplay />}
      </Box>
    </ConversationTooltipManager>
  );
};

// Memoize to prevent re-renders when URL params change (e.g., opening ContactDrawer)
const ConversationsWrapper = React.memo(ConversationsWrapperComponent);

export default ConversationsWrapper;
